import { saveAs } from 'file-saver'
import { toast } from 'react-toastify'

import { Checkbox, DatePicker, DummyInput, Input, Select, Textarea, VersionFileInput } from '@/components/Form'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { OfferDocumentTypes } from '@/features/offers/constants'
import useOfferPR from '@/features/offers/hooks/useOfferPR'
import useUser from '@/features/users/useUser'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { showModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { Button, Divider, Flex, GridItem, Heading, SimpleGrid } from '@chakra-ui/react'
import { CostBudgetType, PRApprovalType } from '@eapproval/offer-shared'
import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { canEditWorkInstructionSection, canPreviewWorkInstructionDocument } from './PRSubmission.helper'

const getEntityId = (value) => value?._id || value?.id || value || ''
const PDF_MIME_TYPE = 'application/pdf'

const PRSubmissionGeneralInfo = ({
  costBudgetIndex,
  vendorSelectionIndex,
  submissionIndex,
  canEditPRSubmissionGeneralField,
  canEditSubcontractorAgreementRequired,
  canUploadSubcontractorAgreement,
  submissionDetail,
}) => {
  const dispatch = useDispatch()
  const { useFetchCostCenter } = useCostCenter()
  const { useUserOptions } = useUser()
  const { useWorkInstructionDocumentPreview } = useOfferPR()
  const previewWorkInstructionDocument = useWorkInstructionDocumentPreview()
  const watchedSubSystems = useWatch({ name: 'subSystems' })
  const subSystems = useMemo(() => watchedSubSystems ?? [], [watchedSubSystems])
  const selectedSubSystemName = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.subSystem` })
  const costBudgetType = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.type` })
  const offerId = useWatch({ name: '_id' })
  const vendorId = useWatch({
    name: `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.vendor`,
  })
  const RFQCode = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.RFQCode` })
  const scopeOfSupply = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.purpose` })

  const { data: allUserOptions } = useUserOptions({
    valueKey: '_id',
    status: 'active',
  })

  const selectedCostCenterId = useMemo(() => {
    if (!Array.isArray(subSystems) || !selectedSubSystemName) {
      return ''
    }

    const selectedSubSystem = subSystems.find((subSystem) => subSystem?.projectName === selectedSubSystemName)

    if (!selectedSubSystem?.profitCenter) {
      return ''
    }

    return selectedSubSystem.profitCenter?._id || selectedSubSystem.profitCenter || ''
  }, [subSystems, selectedSubSystemName])

  const { data: selectedCostCenter } = useFetchCostCenter(selectedCostCenterId || '')
  const workInstructionAllowed = selectedCostCenter?.workInstructionAllowed ?? false

  const canEditWorkInstruction = canEditWorkInstructionSection(canEditPRSubmissionGeneralField, submissionDetail)

  const hasMissingSAPNumber = useMemo(() => {
    const requestedDetails = Array.isArray(submissionDetail?.requestedDetails) ? submissionDetail.requestedDetails : []

    if (requestedDetails.length === 0) {
      return false
    }

    const normalizeText = (value) => (typeof value === 'string' ? value.trim() : '')

    return requestedDetails.some((requestedItem) => normalizeText(requestedItem?.SAPMaterialNumber) === '')
  }, [submissionDetail?.requestedDetails])

  const submissionPath = `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions.${submissionIndex}`

  const handleOpenWorkInstructionHistoryModal = () => {
    dispatch(
      showModal({
        modalType: MODAL_TYPES.WORK_INSTRUCTION_HISTORY,
        modalProps: {
          history: submissionDetail?.history ?? [],
        },
      })
    )
  }

  // Previews the Work Instruction Document from whatever is currently on
  // screen, even unsaved — ids are sent so the server resolves them the same
  // way real issuance does; a missing one renders as a bracketed placeholder
  // rather than failing, since this is routinely used before everything
  // (vendor, TPM) is finalised.
  const handlePreviewWorkInstructionDocument = async () => {
    const approverDetails = Array.isArray(submissionDetail?.approverDetails) ? submissionDetail.approverDetails : []
    const technicalProjectManager = approverDetails.find(
      (approverDetail) => approverDetail?.type === PRApprovalType.TECHNICAL_PROJECT_MANAGER
    )

    // Opened synchronously with the click: a tab opened only after the merge
    // returns is no longer a user gesture, and popup blockers would stop it.
    const previewTab = window.open('', '_blank')

    dispatch(showLoadingOverlay())
    try {
      const response = await previewWorkInstructionDocument.mutateAsync({
        offerId,
        costBudgetIndex,
        vendorSelectionIndex,
        submissionIndex,
        vendorId: getEntityId(vendorId),
        technicalProjectManagerId: getEntityId(technicalProjectManager?.user),
        costCenterId: getEntityId(selectedCostCenterId),
        PRNumber: submissionDetail?.PRNumber ?? '',
        RFQCode: RFQCode ?? '',
        scopeOfSupply: scopeOfSupply ?? '',
        remark: submissionDetail?.requiredWorkInstructionRemark ?? '',
      })

      const contentType = response.headers['content-type'] ?? ''
      const blob = new Blob([response.data], { type: contentType })

      if (contentType.includes(PDF_MIME_TYPE) && previewTab) {
        previewTab.location.href = URL.createObjectURL(blob)
        return
      }

      // Not a PDF (the server could not convert it), or the tab was blocked anyway.
      previewTab?.close()

      const contentDisposition = response.headers['content-disposition']
      let fileName = 'Work Instruction Preview'

      if (contentDisposition && contentDisposition.includes('filename=')) {
        fileName = contentDisposition.split('filename=')[1].replace(/"/g, '')
      }

      saveAs(blob, fileName)
    } catch (err) {
      previewTab?.close()

      let errorMessage = err?.response?.data?.message || err?.message || 'Failed to generate Work Instruction preview'

      if (err?.response?.data instanceof Blob) {
        const blobText = await err.response.data.text()

        try {
          const parsedError = JSON.parse(blobText)
          errorMessage = parsedError?.message || errorMessage
        } catch (parseError) {
          if (blobText) {
            errorMessage = blobText
          }
        }
      }

      toast.error(errorMessage)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <SimpleGrid columns={[2, null, 4]} spacingX="20px" spacingY="10px" py="3">
      <Input
        name={`${submissionPath}.descriptionOfProduct`}
        label="Remarks"
        type="text"
        disabled={!canEditPRSubmissionGeneralField}
        isRequired={false}
        isRow
      />
      <Input
        name={`${submissionPath}.PONumber`}
        label="PO Number"
        type="text"
        disabled={!canEditPRSubmissionGeneralField}
        isRequired={false}
        isRow
      />
      <Checkbox
        name={`${submissionPath}.licenseRequired`}
        label="License Required : (if any) "
        disabled={!canEditPRSubmissionGeneralField}
        isRequired={false}
        isRow
      />
      <DatePicker
        name={`${submissionPath}.expectedDeliveryDate`}
        label="Expected Delivery Date"
        type="text"
        disabled={!canEditPRSubmissionGeneralField}
        isRequired={true}
        isRow
      />
      <Input
        name={`${submissionPath}.deliveryInstructions`}
        label="Delivery Instruction"
        type="text"
        disabled={!canEditPRSubmissionGeneralField}
        isRequired={false}
        isRow
      />
      <VersionFileInput
        name={`${submissionPath}.internalDocument`}
        label="Internal Documents"
        fileType={OfferDocumentTypes.SOURCING_INTERNAL_DOCUMENT}
        canUpload={canEditPRSubmissionGeneralField}
        isMultiple
        showVersion={false}
        isRequired={false}
      />
      <Checkbox
        name={`${submissionPath}.subcontractorAgreementRequired`}
        label="Subcontractor Agreement Required"
        disabled={!canEditSubcontractorAgreementRequired}
        isRequired={false}
        isRow
      />
      {submissionDetail?.subcontractorAgreementRequired && (
        <VersionFileInput
          name={`${submissionPath}.subcontractorAgreement`}
          label={'Subcontractor Agreement '}
          fileType={OfferDocumentTypes.SOURCING_INTERNAL_DOCUMENT}
          canUpload={canUploadSubcontractorAgreement}
          isMultiple
          showVersion={false}
          isRequired={true}
        />
      )}
      <Input
        name={`${submissionPath}.SAPNumber`}
        label="SO# / SAP PR#"
        type="text"
        disabled={!canEditPRSubmissionGeneralField}
        isRequired={false}
        isRow
      />

      {workInstructionAllowed && (
        <GridItem colSpan={[2, null, 4]}>
          <Divider mb="3" />
          <Heading size="sm" mb="3">
            Work Instruction
          </Heading>
          <Flex direction="column" gap="3">
            <Flex align="center" gap="4" wrap="wrap">
              <Checkbox
                name={`${submissionPath}.requiredWorkInstructions`}
                label="Required Work Instructions"
                disabled={!canEditWorkInstruction}
                isRequired={false}
                isRow
                w="320px"
                labelWidth={70}
              />
              {submissionDetail?.requiredWorkInstructions && (
                <Button
                  type="button"
                  colorScheme="blue"
                  variant="outline"
                  size="sm"
                  onClick={handleOpenWorkInstructionHistoryModal}
                >
                  Work Instruction – History &amp; Documents
                </Button>
              )}
              {canPreviewWorkInstructionDocument(submissionDetail) && (
                <Button
                  type="button"
                  colorScheme="gray"
                  variant="outline"
                  size="sm"
                  isLoading={previewWorkInstructionDocument.isPending}
                  onClick={handlePreviewWorkInstructionDocument}
                >
                  Preview Work Instruction Document
                </Button>
              )}
            </Flex>
            {submissionDetail?.requiredWorkInstructions && (
              <Textarea
                name={`${submissionPath}.requiredWorkInstructionRemark`}
                label="Work Instruction - Additional Remarks"
                disabled={!canEditWorkInstruction}
                isRequired={false}
                isRow={false}
              />
            )}
          </Flex>
        </GridItem>
      )}
    </SimpleGrid>
  )
}

export default PRSubmissionGeneralInfo
